import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Toast, { useToast } from '../components/Toast';
import SEO from '../components/SEO';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const { signIn, signInWithGoogle, authLoading } = useAuth();
    const { toast, showToast, hideToast } = useToast();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const result = await signIn(email, password);
        if (result.success) {
            showToast('Welcome back!', 'success');
            setTimeout(() => navigate('/dashboard'), 500);
        } else {
            showToast(result.error || 'Login failed', 'error');
        }
    };

    const handleGoogle = async () => {
        const result = await signInWithGoogle();
        if (!result.success) {
            showToast(result.error || 'Google sign in failed', 'error');
        }
    };

    return (
        <div className="min-h-screen bg-dark-950 flex flex-col items-center justify-center px-4 pt-32 pb-12">
            <SEO
                title="Login"
                description="Sign in to your Share Visuals account to create and manage your visuals."
            />
            {/* Background glow blobs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-primary-600/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-secondary-600/8 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-md animate-fade-in">
                {/* Logo */}
                <div className="text-center mb-10">
                    <h1 className="text-3xl font-bold text-white tracking-tight">Welcome back</h1>
                    <p className="mt-2 text-dark-400 text-base font-medium">Sign in to your account to continue</p>
                </div>

                {/* Card */}
                <div className="card-glass">
                    {/* Google OAuth */}
                    <button
                        id="google-signin-btn"
                        onClick={handleGoogle}
                        disabled={authLoading}
                        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-dark-600 text-dark-200 hover:text-white hover:border-dark-400 hover:bg-white/5 transition-all text-sm font-bold disabled:opacity-50"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Continue with Google
                    </button>

                    <div className="relative my-8">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-white/10" />
                        </div>
                        <div className="relative flex justify-center text-[10px] font-black uppercase tracking-widest text-dark-500">
                            <span className="bg-[#111827] px-4 rounded-full py-0.5">or continue with email</span>
                        </div>
                    </div>

                    {/* Email/Password Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="login-email" className="label">Email address</label>
                            <input
                                id="login-email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="input"
                            />
                        </div>
                        <div>
                            <label htmlFor="login-password" className="label">Password</label>
                            <input
                                id="login-password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="input"
                            />
                        </div>

                        <button
                            id="login-submit-btn"
                            type="submit"
                            disabled={authLoading}
                            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {authLoading && (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            Sign In
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-dark-400">
                        Don't have an account?{' '}
                        <Link to="/signup" className="text-primary-400 hover:text-primary-300 font-medium">
                            Sign up for free
                        </Link>
                    </p>
                </div>
            </div>

            {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        </div>
    );
};

export default Login;
